import { useEditApiKey } from 'api/ApiKeyApi';
import _compact from 'lodash/compact';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer/utils';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { OverflowMenu } from 'ui/actionMenu';
import { AllowedActionMenuElements } from 'ui/actionMenu/types';
import { Delete } from 'ui/icons/action/Delete';
import { Edit24 } from 'ui/icons/action/Edit24';
import { Start } from 'ui/icons/action/Start';
import { Stop } from 'ui/icons/action/Stop';
import { colors } from 'ui/theme/colors';
import { useModalContext } from 'utils/contexts/modal/context';
import { fetchApiKeysRequest } from '../actions';
import { ApiKey } from '../types';
import { formulateEditApiKeyPayload } from '../utils';
import { MODAL_ACTION, ModalState } from './ApiKeyModal.constants';

type Props = {
  apiKey: ApiKey;
};

const StartWithoutStroke = styled(Start)`
  && path {
    fill: ${colors.grey50};
    /**
      Necessary to override the default stylesheet, which applies a grey stroke to svg paths,
      which in this case causes the icon lines to look doubly thick.
    */
    stroke: none;
  }
`;

export const ApiKeyActionMenu = ({ apiKey }: Props) => {
  const { showModal } = useModalContext<ModalState>();
  const dispatch = useDispatch();

  const { mutate: editApiKey } = useEditApiKey({
    onSuccess: () => {
      dispatch(fetchApiKeysRequest());
      dispatch(addPositiveToast('Saved changes.'));
    },
    onError: () => {
      dispatch(addNegativeToast('Unable to update API key.'));
    },
  });

  const toggleApiKeyEnabled = (keyData: ApiKey) => {
    const payload = formulateEditApiKeyPayload(
      { ...keyData },
      { enabled: !keyData.enabled, keyHash: keyData.keyHash },
    );
    editApiKey(payload);
  };

  return (
    <OverflowMenu
      id="action-menu"
      options={_compact([
        {
          id: 'edit-api-key-action',
          onClick: () =>
            showModal({ action: MODAL_ACTION.EDIT_API_KEY, keyIdentifier: apiKey.keyIdentifier }),
          content: (
            <>
              <Edit24 />
              <span>Edit key</span>
            </>
          ),
          as: AllowedActionMenuElements.Button,
        },
        apiKey.enabled && {
          id: 'disable-api-key-action',
          onClick: () =>
            showModal({
              action: MODAL_ACTION.DISABLE_API_KEY,
              apiKey,
            }),
          content: (
            <>
              <Stop />
              <span>Disable key</span>
            </>
          ),
          as: AllowedActionMenuElements.Button,
        },
        !apiKey.enabled && {
          id: 'enable-api-key-action',
          onClick: () => toggleApiKeyEnabled(apiKey),
          content: (
            <>
              <StartWithoutStroke />
              <span>Enable key</span>
            </>
          ),
          as: AllowedActionMenuElements.Button,
        },
        {
          id: 'delete-api-key-action',
          onClick: () =>
            showModal({
              action: MODAL_ACTION.DELETE_API_KEY,
              keyHash: apiKey.keyHash,
              name: apiKey.name,
            }),
          content: (
            <>
              <Delete />
              <span>Delete key</span>
            </>
          ),
          as: AllowedActionMenuElements.Button,
        },
      ])}
    />
  );
};
